import {trans} from '@frontend/helpers'
import {useRegionConfig} from '@frontend/hooks'
import type {RegionConfig} from '@frontend/types/Locales'
import {mdiRefresh} from '@mdi/js'
import {
    type ChangeEvent,
    type SyntheticEvent,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Countdown} from 'swayok-react-mdb-ui-kit/components/Countdown/Countdown'
import {IconButton} from 'swayok-react-mdb-ui-kit/components/Icon/IconButton'
import {Input} from 'swayok-react-mdb-ui-kit/components/Input/Input'
import {HtmlContent} from 'swayok-react-mdb-ui-kit/components/Typography/HtmlContent'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {AnalyticsEventReporter} from '../../../../helpers/AnalyticsEventReporter'

interface LoginFormStep2Props {
    visible: boolean
    phone: string
    verificationCode: string
    verificationCodeError: string
    resendingTimeout: number
    resend: number
    disabled: boolean
    setVerificationCode: (code: string) => void
    onSubmit: () => void
    onResendCode: () => void
}

// Форма ввода проверочного кода с отправкой запроса на авторизацию
export function LoginFormStep2(props: LoginFormStep2Props) {

    const {
        visible,
        phone,
        verificationCode,
        verificationCodeError,
        resendingTimeout,
        resend,
        disabled,
        setVerificationCode,
        onSubmit: propsOnSubmit,
        onResendCode,
    } = props

    const regionConfig = useRegionConfig()
    const verificationCodeConfig: RegionConfig['verificationCode'] = regionConfig.verificationCode

    const [
        isCountdownActive,
        setIsCountdownActive,
    ] = useState<boolean>(false)

    const inputRef = useRef<HTMLInputElement>(null)

    // Контроль активности обратного отсчета.
    useEffect(() => {
        setIsCountdownActive(visible && !disabled)
    }, [visible, disabled, resend])

    // Сфокусировать поле ввода кода при отображении формы.
    useLayoutEffect(() => {
        if (visible && !disabled && inputRef.current) {
            // Без тайм-аута не работает.
            setTimeout(() => {
                inputRef.current?.focus()
            }, 200)
        }
    }, [visible, disabled, resend])

    // Отправка кода на проверку.
    const onSubmit = useEventCallback((
        e: SyntheticEvent
    ): void => {
        e.preventDefault()
        AnalyticsEventReporter.loginFormVerifySmsCodeButtonClicked(
            disabled,
            verificationCode.trim().length > 0
        )
        if (!disabled) {
            propsOnSubmit()
        }
    })

    // Изменение значения кода проверки.
    const onVerificationCodeChange = useEventCallback((
        e: ChangeEvent<HTMLInputElement>
    ): void => {
        setVerificationCode(e.target.value)
    })

    return (
        <div className="ps-4 pe-4 pt-2 pb-2">
            <div className="fs-5 fw-500">
                {trans().auth.verification_code.header}
            </div>
            <HtmlContent
                block
                className="mt-3 fs-7"
                html={trans().auth.verification_code.code_sent_to_phone(phone)}
            />
            <form
                className="mb-0 mt-4"
                onSubmit={onSubmit}
            >
                <Input
                    inputRef={inputRef}
                    label={trans().auth.verification_code.verification_code}
                    value={verificationCode}
                    placeholder={verificationCodeConfig.placeholder}
                    maxLength={verificationCodeConfig.digits}
                    className="text-center"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    active
                    disabled={disabled}
                    allowedChars={/\d/}
                    invalid={verificationCodeError.length > 0}
                    validationMessage={verificationCodeError}
                    style={{
                        letterSpacing: 8,
                    }}
                    onChange={onVerificationCodeChange}
                >
                    {visible && !disabled && isCountdownActive && (
                        <div className="input-countdown flex-center text-muted">
                            <Countdown
                                seconds={resendingTimeout}
                                onZero={() => {
                                    setIsCountdownActive(false)
                                }}
                            />
                        </div>
                    )}
                    {visible && !disabled && !isCountdownActive && (
                        <div className="input-countdown flex-center">
                            <IconButton
                                path={mdiRefresh}
                                color="blue"
                                tooltip={trans().auth.verification_code.resend}
                                onClick={onResendCode}
                                disabled={disabled || !visible}
                            />
                        </div>
                    )}
                </Input>
            </form>
            <Button
                color="blue"
                className="full-width"
                onClick={onSubmit}
                disabled={disabled}
            >
                {trans().auth.verification_code.do_confirm}
            </Button>
        </div>
    )
}
